// src/llmConnections/components/RegisterLlmConnectionModal.jsx
//
// Registration form for an LLM connection. Unlike
// connectors/components/RegisterConnectionModal.jsx, this also asks which
// *level* to register at (org/account/project/personal) — and only offers
// a level the caller actually holds connections:write for (personal always
// available; org/account/project checked via useAuth()'s
// hasGlobalPermission/hasPermission, the same pattern
// accounts/pages/ProjectsPage.jsx's canCreate already uses). Offering a
// level the caller can't use would just trade a disabled button for a
// guaranteed 403 on submit.

import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle } from "lucide-react";
import { Modal, FormField, Input, PasswordInput, Select, Button, Alert, useDiscardConfirm } from "../../shared/ui";
import { useAuth } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { LLM_PROVIDERS, LLM_LEVEL_OPTIONS, getLlmProvider } from "../constants";

export default function RegisterLlmConnectionModal({ open, onClose, onRegister }) {
  const { hasPermission, hasGlobalPermission } = useAuth();
  const { accounts, engagements, currentAccount } = useWorkspace();

  const canOrg = hasGlobalPermission("connections:write");
  const writableAccounts = useMemo(
    () => (canOrg ? accounts : accounts.filter((a) => hasPermission("connections:write", { accountId: a.account_id }))),
    [accounts, canOrg, hasPermission]
  );
  // Projects list is whatever's currently loaded (the current Account's
  // engagements, per WorkspaceContext) — matches this app's established
  // pattern of scoping project pickers to the topbar's Account selection
  // (see accounts/pages/ProjectsPage.jsx) rather than a second,
  // independent cross-account project picker.
  const writableEngagements = useMemo(
    () => (canOrg || (currentAccount && writableAccounts.some((a) => a.account_id === currentAccount.account_id))
      ? engagements
      : engagements.filter((e) => hasPermission("connections:write", { accountId: e.account_id, projectId: e.engagement_id }))),
    [engagements, canOrg, currentAccount, writableAccounts, hasPermission]
  );

  const availableLevels = useMemo(
    () => LLM_LEVEL_OPTIONS.filter((l) => {
      if (l.value === "personal") return true;
      if (l.value === "org") return canOrg;
      if (l.value === "account") return writableAccounts.length > 0;
      return writableEngagements.length > 0; // project
    }),
    [canOrg, writableAccounts, writableEngagements]
  );

  const [level, setLevel] = useState("personal");
  const [accountId, setAccountId] = useState("");
  const [engagementId, setEngagementId] = useState("");
  const [providerValue, setProviderValue] = useState(LLM_PROVIDERS[0].value);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dirty, setDirty] = useState(false);

  const provider = getLlmProvider(providerValue);

  function reset() {
    setLevel("personal");
    setAccountId("");
    setEngagementId("");
    setProviderValue(LLM_PROVIDERS[0].value);
    setValues({});
    setErrors({});
    setSubmitting(false);
    setResult(null);
    setDirty(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !result, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!provider) return;

    const nextErrors = {};
    provider.fields.forEach((f) => {
      if (f.showIf && !f.showIf(values)) return; // hidden by the current authType choice — nothing to validate
      if (f.required && !String(values[f.name] || "").trim()) {
        nextErrors[f.name] = `${f.label} is required.`;
      }
    });
    if (level === "account" && !accountId) nextErrors.account = "Choose which Account this connection belongs to.";
    if (level === "project" && !engagementId) nextErrors.project = "Choose which Project this connection belongs to.";
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { testResult } = await onRegister({
        level,
        accountId: level === "account" ? Number(accountId) : undefined,
        engagementId: level === "project" ? engagementId : undefined,
        provider: providerValue,
        values,
      });
      setResult(testResult);
    } catch (err) {
      setResult({ status: "failed", message: err?.message || "Registration failed." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal open={open} title="Register LLM connection" onClose={requestClose}>
        {result ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant={result.status === "passed" ? "success" : "danger"}
              icon={result.status === "passed" ? CheckCircle2 : XCircle}
              title={result.status === "passed" ? "Connection saved and verified" : "Connection saved, but the test failed"}
            >
              {result.message}
            </Alert>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={reset}>Register another</Button>
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <FormField label="Who can use this connection" required>
              <Select
                options={availableLevels}
                value={level}
                onChange={(e) => { setLevel(e.target.value); setAccountId(""); setEngagementId(""); setDirty(true); }}
              />
            </FormField>

            {level === "account" && (
              <FormField label="Account" required error={Boolean(errors.account)} errorText={errors.account}>
                <Select
                  options={[{ value: "", label: "Choose an Account…" }, ...writableAccounts.map((a) => ({ value: a.account_id, label: a.name }))]}
                  value={accountId}
                  onChange={(e) => { setAccountId(e.target.value); setDirty(true); }}
                />
              </FormField>
            )}

            {level === "project" && (
              <FormField label="Project" required error={Boolean(errors.project)} errorText={errors.project}>
                <Select
                  options={[{ value: "", label: "Choose a Project…" }, ...writableEngagements.map((eng) => ({ value: eng.engagement_id, label: eng.client_name }))]}
                  value={engagementId}
                  onChange={(e) => { setEngagementId(e.target.value); setDirty(true); }}
                />
              </FormField>
            )}

            {LLM_PROVIDERS.length > 1 && (
              <FormField label="Provider" required>
                <Select
                  options={LLM_PROVIDERS.map((p) => ({ value: p.value, label: p.label }))}
                  value={providerValue}
                  onChange={(e) => { setProviderValue(e.target.value); setValues({}); setErrors({}); setDirty(true); }}
                />
              </FormField>
            )}

            {provider?.fields.filter((f) => !f.showIf || f.showIf(values)).map((f) => (
              <FormField
                key={f.name}
                label={f.label}
                required={f.required}
                helperText={f.helperText}
                error={Boolean(errors[f.name])}
                errorText={errors[f.name]}
              >
                {f.type === "password" ? (
                  <PasswordInput value={values[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} placeholder={f.placeholder} />
                ) : f.type === "select" ? (
                  <Select options={f.options} value={values[f.name] || f.options?.[0]?.value || ""} onChange={(e) => setField(f.name, e.target.value)} />
                ) : (
                  <Input value={values[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} placeholder={f.placeholder} />
                )}
              </FormField>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="primary" loading={submitting}>Register &amp; test</Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

RegisterLlmConnectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
};
